import React, { useEffect, useRef, useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { IconButton, Button, SvgIcon } from '@mui/material';

import {
  Close as CloseIcon,
  OpenInNew as PopOutIcon,
  ExitToApp as PopInIcon,
  Assessment as StatsIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
} from '@mui/icons-material';

import StatsForNerds from './StatsForNerds';
import JesterHat, { TrackedJesterHat } from './JesterHat';
import remoteAudio from '../services/remoteAudio';

// MUI v4's icon set has no thumbtack ("push_pin" only shipped in v5+), so we
// inline the official Material Symbols push_pin paths via SvgIcon. Filled =
// pinned (header locked open); outlined = unpinned (header auto-hides).
const PinnedIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M16 9V4l1 0c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1l1 0v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z" />
  </SvgIcon>
);
const UnpinnedIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M14 4v5c0 1.12.37 2.16 1 3H9c.65-.86 1-1.9 1-3V4h4m3-2H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3V4h1c.55 0 1-.45 1-1s-.45-1-1-1z" />
  </SvgIcon>
);

// Tiny live voice-level pill (top-left of the tile). Reads the user's audio
// graph remoteAudio, the publish vocal chain for self —
// at 5 Hz and mutates the fill width without re-rendering. Shows empty when
// the user has no live audio (getLevel returns zeros).
// Currently DISABLED (render site commented out below) pending visual tuning.
// eslint-disable-next-line no-unused-vars
const SpeakingMeter = ({ userId, isSelf, classes }) => {
  const fillRef = useRef(null);
  useEffect(() => {
    const tick = () => {
      // const { rms } = isSelf ? voiceChain.getLevel() : remoteAudio.getLevel(userId);
      const { rms } = remoteAudio.getLevel(userId);
      // Map -50..-6 dBFS onto 0..100% — speech sits well inside this window.
      const db = rms > 0 ? 20 * Math.log10(rms) : -100;
      const frac = Math.min(1, Math.max(0, (db + 50) / 44));
      const node = fillRef.current;
      if (node) node.style.width = `${Math.round(frac * 100)}%`;
    };
    tick();
    const timer = setInterval(tick, 200);
    return () => clearInterval(timer);
  }, [userId, isSelf]);
  return (
    <div className={classes.speaks}>
      <div ref={fillRef} className={classes.volume} />
    </div>
  );
};

// Coarse-pointer (touch) detection — iPad/phones have no hover, so the
// auto-hiding tile header is revealed on tap there instead of on hover.
// Evaluated once at module load.
const IS_TOUCH =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: none), (pointer: coarse)').matches;

// Small wrapper that imperatively binds a MediaStream to a <video>'s srcObject
// (srcObject can't be set as a JSX prop). Re-binds whenever the stream changes.
//
// muted defaults true; callers pass muted={false} for OTHER users' tiles so
// their bundled audio track plays. Your OWN tile stays muted (no echo).
// controls: native <video controls> gives a hover-revealed bar (volume /
// pause / fullscreen) for free. Enabled on OTHER users' tiles so viewers can
// set per-cam volume; your OWN tile leaves it off (it's a muted local preview).
const VideoTile = ({ stream, className, id, muted = true, controls = false, onEl }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el && stream && el.srcObject !== stream) {
      el.srcObject = stream;
    }
  }, [stream]);
  // Hand the underlying element up to the parent (stats panel reads its
  // clientWidth/Height for "Viewport" and .volume for the volume row).
  useEffect(() => {
    if (onEl) onEl(ref.current);
    return () => onEl && onEl(null);
  }, [onEl]);
  return (
    <video
      id={id}
      ref={ref}
      className={className}
      autoPlay
      playsInline
      muted={muted}
      controls={controls}
      // Hide the native download/⋮ menu so the bar shows only playback controls.
      controlsList="nodownload noremoteplayback"
      disablePictureInPicture
    />
  );
};


// Height of the title-bar band reserved above the video. The header is
// absolutely positioned within this band so it never overlays the picture.
const HEADER_H = 32;

const useStyles = makeStyles()((theme, _params, classes) => ({
  webcamsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    height: '100%',
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  webcamContainer: {
    position: 'relative',
    height: '100%',
    width: 'auto',
    // The panel is the drag-to-resize unit (grab the bottom-right corner). The
    // 4:3 aspect lock lives on the video frame (.webcamSwfContainer).
    //
    // The top band (HEADER_H) is reserved in BOTH pin modes, so the video has
    // the exact same size/position whether the header is pinned or hidden — the
    // header lives ABOVE the video (never over it) and never resizes it. The
    // only difference between modes is header visibility:
    //  - PINNED  : header always shown in the band.
    //  - UNPINNED: header hidden in the band, revealed on hover/tap.
    // width: 280,
    paddingTop: HEADER_H,
    minWidth: 180,
    maxWidth: '100%',
    // resize: 'both',
    backgroundColor: theme.palette.custom.videoTileBg,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.custom.line}`,
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
    },
  },
  // Docked (in-grid) tile: no border, and the tile background matches the grid
  // (videoAreaBg — black on midnight) so the tile blends into the video area.
  // Popped-out tiles keep the bordered, raised look above.
  webcamContainerDocked: {
    border: 'none',
    backgroundColor: theme.palette.custom.videoAreaBg,
  },
  // Desktop hover reveal (unpinned only): bring the hidden header in. Touch
  // devices have no hover, so they use the tap state instead (inline style on
  // the header).
  webcamContainerHoverReveal: {
    [`&:hover .${classes.webcamHeaderOverlay}`]: {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  resizable: {
    resize: 'both',
  },
  // Drop target being hovered during a tile drag — a left-edge accent bar marks
  // the insertion point without shifting layout.
  dropTarget: {
    boxShadow: `inset 3px 0 0 0 ${theme.palette.primary.main}`,
  },
  // The tile currently being dragged — dimmed so the drag is legible.
  dragging: {
    opacity: 0.4,
  },
  // Drag handle is the header bar only, so dragging the tile doesn't fight the
  // native <video> controls (volume slider / scrubber) inside the video box.
  dragHandle: {
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  // The header is the SAME solid bar in both modes — a panel-colored strip
  // pinned to the top edge (position:absolute, so it never resizes the video).
  //   - PINNED  : the container reserves a band (paddingTop) so the bar sits
  //               ABOVE the video; always visible.
  //   - UNPINNED: no reserved band — the bar floats over the very top edge of
  //               the video, hidden until hover/tap, then shown identically to
  //               the pinned bar (solid, not a translucent overlay).
  webcamHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: HEADER_H,
    boxSizing: 'border-box',
    padding: theme.spacing(0.5, 0.75),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
    zIndex: 2,
    // backgroundColor: theme.palette.custom.panel,
    backgroundColor: theme.palette.custom.videoTileBg,
    borderBottom: `1px solid ${theme.palette.custom.line}`,
  },
  // Unpinned: same solid bar, just hidden until revealed (hover/tap).
  webcamHeaderOverlay: {
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.2s ease',
  },
  webcamHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0, // let the username truncate instead of pushing controls off-screen
    flex: 1,
  },
  webcamHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  username: {
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  headerButton: {
    padding: theme.spacing(0.25, 1),
    minWidth: 'auto',
    fontSize: '0.7rem',
    lineHeight: 1.4,
    flexShrink: 0,
    '&.privateBtn': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.custom.accentContrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  headerIcon: {
    padding: 4,
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  qualitySelect: {
    fontSize: '0.7rem',
    color: theme.palette.text.primary,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: 4,
    padding: '0 4px',
    '&:before, &:after': { display: 'none' },
    '& .MuiSelect-select': { paddingRight: 18 },
    '& .MuiSelect-icon': { color: theme.palette.text.secondary },
  },
  webcamSwfContainer: {
    // The video frame: a 4:3 block filling the area BELOW the header band. The
    // 4:3 aspect lock lives here now (moved off the outer container) so the
    // picture keeps its ratio while the header adds its own height on top.
    position: 'relative',
    width: '100%',
    height: '100%',
    aspectRatio: '4 / 3',
    backgroundColor: '#000000',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  placeholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    backgroundImage: `linear-gradient(135deg, ${theme.palette.custom.videoTileBg} 0%, ${theme.palette.custom.panel} 100%)`,
  },
  // Covers the (still-mounted) <video> when the publisher pauses their cam, so
  // the tile shows "camera paused" rather than a frozen last frame. The video
  // element stays mounted underneath so its bundled AUDIO keeps playing.
  pausedOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    backgroundImage: `linear-gradient(135deg, ${theme.palette.custom.videoTileBg} 0%, ${theme.palette.custom.panel} 100%)`,
    zIndex: 1,
  },
  speaks: {
    position: 'absolute',
    // Top-left, not bottom — the native control bar occupies the bottom on
    // hover; keeping the voice-activity meter up top avoids overlap.
    top: theme.spacing(1),
    left: theme.spacing(1),
    width: 40,
    height: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 3,
    overflow: 'hidden',
    zIndex: 2,
    pointerEvents: 'none', // never intercept clicks meant for the video controls
  },
  volume: {
    height: '100%',
    backgroundColor: theme.palette.secondary.main,
    width: '0%',
    transition: 'width 0.1s ease',
    '&.speaking': {
      width: '80%',
    },
  },
}));

export default function VideoParticipantTile({
  participant,
  viewQuality,
  // True when this participant is wearing the "noodle shame" jester hat.
  noodled = false,
  // Live head transform for this participant (the noodler streams it): { cx, cy,
  // wFrac, rollDeg } normalized to the video frame, or null. When present the hat
  // FOLLOWS their head; when null (cam off, tracking unavailable, face lost) we
  // fall back to the static top-corner hat.
  noodleTrack = null,
  docked = false,
  // Drag-to-reorder state/handlers (docked grid only). isDragging dims this
  // tile while it's the drag source; isDropTarget shows the insertion accent
  // when another tile is being dragged over it. onDragStart/End fire from the
  // header (the drag handle); onDragOver/Drop/Leave make the whole tile a drop
  // target.
  isDragging = false,
  isDropTarget = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onDragLeave,
  onPrivateRequest,
  onQualityChange,
  onWindowOut,
  onWindowIn,
  onClose,
  // Stats-panel open state + dragged position are CONTROLLED by the parent
  // (App.js, keyed by userId) so they survive this tile unmounting/remounting
  // when the user is popped out/in. statsAnchor is the first-open position;
  // statsPos is the last position the user dragged the panel to.
  statsOpen = false,
  statsAnchor = null,
  statsPos = null,
  onToggleStats,
  onCloseStats,
  onStatsPositionChange,
  // Per-user listening chain (remoteAudio): opens the App-level volume popover
  // anchored to this tile's speaker button. remoteVolume (0-400, from App's
  // remoteAudioPrefs mirror) drives the muted-vs-live icon state.
  onOpenVolume,
  remoteVolume = 100,
}) {
  const { classes, cx } = useStyles();
  // Pin the header open (always shown) vs. let it auto-hide and reveal on hover
  // (desktop) / tap (touch). Session-only, per tile. Docked (in-grid) tiles
  // default to UNPINNED (auto-hiding) for a cleaner grid; popped-out windows
  // are always pinned (the pin toggle is hidden for them — see below).
  const [pinned, setPinned] = useState(!docked);
  // Touch-only reveal latch (desktop uses CSS :hover). Tapping the tile toggles
  // the unpinned header + control bar; pinned tiles ignore it (header is fixed).
  const [revealed, setRevealed] = useState(false);
  // The stats panel free-floats (portaled to <body>). videoEl is captured from
  // <VideoTile> so the panel can read the rendered viewport size + current
  // volume. Open state itself is controlled by the parent (see props above).
  const [videoEl, setVideoEl] = useState(null);
  const containerRef = useRef(null);
  // The video FRAME element + its rendered pixel size, needed to place the
  // head-tracked hat (whose transform is normalized to the frame). Only observed
  // while a tracked hat is actually showing, to avoid idle ResizeObserver churn.
  const frameRef = useRef(null);
  const [frameSize, setFrameSize] = useState({ w: 0, h: 0 });
  const showTrackedHat = noodled && !!noodleTrack;
  useEffect(() => {
    if (!showTrackedHat || !frameRef.current) return undefined;
    const el = frameRef.current;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setFrameSize({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showTrackedHat]);

  const toggleStats = useCallback((e) => {
    if (e) e.stopPropagation();
    // Capture the tile's current on-screen rect so the parent can place the
    // panel near it the first time it opens.
    const rect = containerRef.current
      ? (() => {
          const r = containerRef.current.getBoundingClientRect();
          return { left: r.left, top: r.top };
        })()
      : null;
    if (onToggleStats) onToggleStats(participant.id, rect);
  }, [onToggleStats, participant.id]);

  const handleTileClick = useCallback(() => {
    if (IS_TOUCH && !pinned) setRevealed((v) => !v);
  }, [pinned]);

  const containerClass = cx(
    classes.webcamContainer,
    // Docked (in-grid): borderless, grid-colored background.
    docked && classes.webcamContainerDocked,
    // Hover reveal: always wired (the hidden-header selector is inert when
    // pinned; the control-bar selector reveals the bar in both modes).
    !IS_TOUCH && classes.webcamContainerHoverReveal,
    // Drag-to-reorder visual state (classes only present on docked grid tiles).
    isDragging && classes.dragging,
    isDropTarget && classes.dropTarget,
  );

  // Same solid bar in both modes; unpinned just adds the hidden-until-revealed
  // behavior (webcamHeaderOverlay).
  const headerClass = cx(
    classes.webcamHeader,
    classes.dragHandle,
    !pinned && classes.webcamHeaderOverlay,
  );

  // On touch + unpinned, force the header visible via inline style when the
  // tile is tapped open (mirrors the control bar's touch handling).
  const headerTouchStyle =
    !pinned && IS_TOUCH
      ? { opacity: revealed ? 1 : 0, pointerEvents: revealed ? 'auto' : 'none' }
      : undefined;

  return (
    <div
      ref={containerRef}
      className={containerClass}
      data-id={participant.id}
      onClick={handleTileClick}
      // The whole docked tile is a drop target for reordering; the drag SOURCE
      // is the header bar only (see draggable + onDragStart below), so dragging
      // never fights the native <video> controls inside the video box.
      onDragOver={docked && onDragOver ? (e) => onDragOver(e, participant) : undefined}
      onDrop={docked && onDrop ? (e) => onDrop(e, participant) : undefined}
      onDragLeave={docked ? onDragLeave : undefined}
    >
      <div
        className={headerClass}
        style={headerTouchStyle}
        // Clicks on the header (its buttons) shouldn't bubble to the tile's
        // touch reveal-toggle — only tapping the video toggles the header.
        onClick={(e) => e.stopPropagation()}
        // The header is the drag handle for reordering (docked grid tiles only).
        draggable={docked && !!onDragStart}
        onDragStart={(e) => onDragStart && onDragStart(e, participant)}
        onDragEnd={onDragEnd}
      >
        <div className={classes.webcamHeaderLeft}>
          {!participant.isSelf && (
            <Button
              className={cx(classes.headerButton, 'privateBtn')}
              onClick={() => onPrivateRequest(participant.id)}
              size="small"
            >
              Private
            </Button>
          )}

          <span className={classes.username}>
            {participant.username}
          </span>
        </div>

        <div className={classes.webcamHeaderRight}>
          {/* <Select
            className={classes.qualitySelect}
            value={viewQuality}
            onChange={(e) =>
              onQualityChange(participant.id, e.target.value)
            }
            disableUnderline
            title={
              participant.isSelf
                ? 'Output quality'
                : "View quality (capped at publisher's)"
            }
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </Select> */}

          {/* Pin toggle only on docked (in-grid) tiles. Popped-out windows are
              always pinned — an auto-hiding header makes no sense there. */}
          {docked && (
          <IconButton
            className={classes.headerIcon}
            onClick={(e) => {
              e.stopPropagation();
              setPinned((p) => !p);
              setRevealed(false);
            }}
            size="small"
            title={pinned ? 'Unpin header (auto-hide)' : 'Pin header (always show)'}
          >
            {pinned
              ? <PinnedIcon fontSize="small" />
              : <UnpinnedIcon fontSize="small" />}
          </IconButton>
          )}

          {onWindowOut && (
          <IconButton
            className={classes.headerIcon}
            onClick={() => onWindowOut(participant.id)}
            size="small"
            title="Window outside"
          >
            <PopOutIcon fontSize="small" />
          </IconButton>
          )}

          {onWindowIn && (
          <IconButton
            className={classes.headerIcon}
            onClick={() => onWindowIn(participant.id)}
            size="small"
            title="Window inside"
          >
            <PopInIcon fontSize="small" />
          </IconButton>
          )}

          {/* {!participant.isSelf && onOpenVolume && ( */}
          {onOpenVolume && (
          <IconButton
            className={classes.headerIcon}
            onClick={(e) => onOpenVolume(participant.id, e.currentTarget)}
            size="small"
            title={`Volume for ${participant.username}`}
          >
            {remoteVolume === 0
              ? <VolumeOffIcon fontSize="small" />
              : <VolumeUpIcon fontSize="small" />}
          </IconButton>
          )}

          <IconButton
            className={classes.headerIcon}
            onClick={toggleStats}
            size="small"
            title="Stats for nerds"
          >
            <StatsIcon fontSize="small" />
          </IconButton>

          <IconButton
            className={classes.headerIcon}
            onClick={() => onClose(participant.id)}
            size="small"
            title={
              participant.isSelf
                ? 'Disable my webcam'
                : 'Close'
            }
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className={classes.webcamSwfContainer} ref={frameRef}>
        {/* Noodle shame hat. If the noodler is streaming a live head transform
            (their cam on + tracking works), the hat FOLLOWS their head across the
            frame. Otherwise it falls back to a static hat perched top-left (fixed
            56px; the tile clips overflow so it stays inside the frame). */}
        {noodled && (
          showTrackedHat ? (
            <TrackedJesterHat
              transform={noodleTrack}
              tileW={frameSize.w}
              tileH={frameSize.h}
              title={`${participant.username} noodled it 🍜`}
            />
          ) : (
            <JesterHat
              size={56}
              title={`${participant.username} noodled it 🍜`}
              style={{ position: 'absolute', left: 8, top: 4, transform: 'none', zIndex: 3 }}
            />
          )
        )}
        {participant.hasVideo && participant.stream ? (
          <>
            <VideoTile
              id={`video_${participant.id}`}
              className={classes.video}
              stream={participant.stream}
              // Your OWN tile opens MUTED so unmuting it doesn't feed your mic
              // straight back into your ears; you can unmute via the native
              // controls to self-listen (the SFU-relayed audio others receive).
              muted={participant.isSelf}
              // Native controls (volume/pause/fullscreen) on every tile — on the
              // self tile they're what lets you unmute to hear yourself.
              controls={true}
              onEl={setVideoEl}
            />


            {participant.videoPaused && (
              <div className={classes.pausedOverlay}>
                {participant.isSelf
                  ? 'Camera off'
                  : `${participant.username} turned off their camera`}
              </div>
            )}
          </>
        ) : (
          <div className={classes.placeholder}>
            {participant.username} (connecting…)
          </div>
        )}

        {/* Speaking meter (top-left pill): live voice level from the user's
            audio graph — the listening chain for remote tiles, the publish
            chain for the self tile. Polled at 5 Hz via SpeakingMeter below;
            reads 0 (empty pill) when they have no mic. */}
        {/* {participant.hasVideo && participant.stream && (
          <SpeakingMeter
            userId={participant.id}
            isSelf={participant.isSelf}
            classes={classes}
          />
        )} */}
      </div>

      {/* Free-floating stats window. Portals itself to <body> (escapes this
          tile's overflow:hidden) and is draggable anywhere on screen. Open state
          is owned by App.js (keyed by userId), so it persists when the tile is
          popped out/in; closing/unwatching clears it there. */}
      {statsOpen && (
        <StatsForNerds
          producerId={participant.producerId}
          isSelf={participant.isSelf}
          viewEl={videoEl}
          username={participant.username}
          userId={participant.id}
          anchorRect={statsAnchor}
          savedPos={statsPos}
          onPositionChange={(pos) =>
            onStatsPositionChange && onStatsPositionChange(participant.id, pos)
          }
          onClose={() => onCloseStats && onCloseStats(participant.id)}
        />
      )}
    </div>
  )
}